import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft, Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/opportunities/new')({
  component: NewOpportunityPage,
})

type PipelineRow = { id: string; name: string; isDefault: boolean }
type StageRow = {
  id: string
  pipelineId: string
  name: string
  probability: number
  sortOrder: number
}
type AccountRow = { id: string; name?: string | null }

/**
 * Dedicated create form for opportunities. We don't use LayoutAwareForm
 * here because the create flow needs tightly-coupled pickers —
 * pipeline -> stage (dependent) and account (typeahead) — with an auto-
 * filled `probability` sourced from the selected stage. Those cross-
 * field behaviors don't fit cleanly into the layout renderer.
 */
function NewOpportunityPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [accountQuery, setAccountQuery] = useState('')
  const [debouncedAccountQuery, setDebouncedAccountQuery] = useState('')
  const [accountId, setAccountId] = useState('')
  const [pipelineId, setPipelineId] = useState('')
  const [stageId, setStageId] = useState('')
  const [amount, setAmount] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [probability, setProbability] = useState<number | ''>('')

  // Debounce the account query — the typeahead fires accounts.list with q.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedAccountQuery(accountQuery), 250)
    return () => clearTimeout(handle)
  }, [accountQuery])

  const { data: pipelineData } = useQuery(orpc.crm.pipelines.list.queryOptions({}))
  const pipelines = (pipelineData as PipelineRow[] | undefined) ?? []

  useEffect(() => {
    if (!pipelineId) {
      const def = pipelines.find((p) => p.isDefault)?.id ?? pipelines[0]?.id ?? ''
      if (def) setPipelineId(def)
    }
  }, [pipelineId, pipelines])

  const { data: stageData } = useQuery(
    orpc.crm.stages.list.queryOptions({
      input: { pipelineId: pipelineId || '00000000-0000-0000-0000-000000000000' },
      enabled: !!pipelineId,
    }),
  )
  const stages = (stageData as StageRow[] | undefined) ?? []
  const selectedStage = useMemo(() => stages.find((s) => s.id === stageId), [stages, stageId])

  // When the stage changes, auto-fill probability from the stage default.
  // The user can still override by editing the field manually.
  useEffect(() => {
    if (selectedStage) setProbability(selectedStage.probability)
  }, [selectedStage])

  const { data: accountData } = useQuery(
    orpc.crm.accounts.list.queryOptions({
      input: { limit: 20, offset: 0, q: debouncedAccountQuery || undefined },
    }),
  )
  const accountResults = (accountData as AccountRow[] | undefined) ?? []

  const createMutation = useMutation(
    orpc.crm.opportunities.create.mutationOptions({
      onSuccess: (result) => {
        toast.success(t('crm.opportunity.entity'))
        queryClient.invalidateQueries({ queryKey: orpc.crm.opportunities.list.key() })
        const created = result as { id: string }
        navigate({
          to: '/crm/opportunities/$opportunityId',
          params: { opportunityId: created.id },
        })
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const canSubmit =
    name.trim() !== '' &&
    accountId !== '' &&
    pipelineId !== '' &&
    stageId !== '' &&
    amount.trim() !== '' &&
    currency.trim().length === 3

  const handleSubmit = () => {
    if (!canSubmit) return
    createMutation.mutate({
      name,
      accountId,
      pipelineId,
      stageId,
      amount,
      currency,
      probability: typeof probability === 'number' ? probability : undefined,
    })
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/crm/opportunities' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('crm.common.cancel')}
        </Button>
        <h1 className="text-2xl font-bold">{t('crm.opportunity.newOpportunity')}</h1>
      </div>

      <div className="space-y-4 border rounded-md p-4 bg-card">
        <div className="space-y-1">
          <Label htmlFor="opp-name">{t('crm.opportunity.name')}</Label>
          <Input id="opp-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="opp-account">{t('crm.opportunity.account')}</Label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="opp-account"
              className="pl-8"
              placeholder={`${t('crm.common.search')}…`}
              value={accountQuery}
              onChange={(e) => {
                setAccountQuery(e.target.value)
                // typing clears any previous selection
                setAccountId('')
              }}
            />
          </div>
          {accountResults.length > 0 && (!accountId || accountQuery !== '') && (
            <div className="border rounded-md max-h-48 overflow-y-auto text-sm">
              {accountResults.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={`w-full text-left p-2 hover:bg-accent ${
                    accountId === a.id ? 'bg-accent' : ''
                  }`}
                  onClick={() => {
                    setAccountId(a.id)
                    setAccountQuery(a.name ?? '')
                  }}
                >
                  {a.name ?? a.id}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>{t('crm.opportunity.pipeline')}</Label>
            <Select
              value={pipelineId}
              onValueChange={(v) => {
                setPipelineId(v)
                // Reset dependent stage when the pipeline changes.
                setStageId('')
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('crm.opportunity.pipeline')} />
              </SelectTrigger>
              <SelectContent>
                {pipelines.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>{t('crm.opportunity.stage')}</Label>
            <Select value={stageId} onValueChange={setStageId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={pipelineId ? t('crm.opportunity.stage') : t('crm.common.required')}
                />
              </SelectTrigger>
              <SelectContent>
                {stages.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1 col-span-2">
            <Label htmlFor="opp-amount">{t('crm.opportunity.amount')}</Label>
            <Input
              id="opp-amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="opp-currency">{t('crm.opportunity.currency')}</Label>
            <Input
              id="opp-currency"
              maxLength={3}
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="opp-probability">{t('crm.opportunity.probability')} (%)</Label>
          <Input
            id="opp-probability"
            type="number"
            min={0}
            max={100}
            value={probability === '' ? '' : probability}
            onChange={(e) => {
              const v = e.target.value
              setProbability(v === '' ? '' : Number(v))
            }}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={() => navigate({ to: '/crm/opportunities' })}>
            {t('crm.common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || createMutation.isPending}>
            {createMutation.isPending ? t('crm.common.creating') : t('crm.common.create')}
          </Button>
        </div>
      </div>
    </div>
  )
}
